/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * Tool Execution Service
 * 
 * Handles execution of SkillShop tools via the /api/tools/execute endpoint.
 */

import { authenticatedFetch } from '@shared/lib/api';

/**
 * Result from executing a tool
 */
export interface ToolExecutionResult {
  success: boolean;
  result?: unknown;
  error?: string;
}

/**
 * Execute a SkillShop tool
 * 
 * @param tool - The tool to execute (e.g., 'issue-manager')
 * @param operation - The operation to perform (e.g., 'create', 'delete')
 * @param params - Additional parameters for the tool operation
 * @returns The tool execution result
 */
export async function executeTool(
  tool: string,
  operation: string,
  params: Record<string, unknown> = {}
): Promise<ToolExecutionResult> {
  try {
    const requestBody = {
      tool,
      operation,
      ...params,
    };

    const response = await authenticatedFetch('/api/tools/execute', {
      method: 'POST',
      body: requestBody,
    });

    if (!response.ok) {
      const errorText = await response.text();
      return {
        success: false,
        error: `Tool execution failed (${response.status}): ${errorText}`,
      };
    }

    const result = await response.json();
    return {
      success: true,
      result,
    };
  } catch (error: any) {
    console.error('[SkillShop Tools] Error executing tool:', error);
    return {
      success: false,
      error: error.message || 'Failed to execute tool',
    };
  }
}

/**
 * Create an issue using the issue-manager tool
 * 
 * @param issueData - The issue data to create
 * @returns The tool execution result
 */
export async function createIssue(issueData: Record<string, unknown>): Promise<ToolExecutionResult> {
  return executeTool('issue-manager', 'create', { issueData });
}

/**
 * Delete an issue using the issue-manager tool
 * 
 * @param issueId - The ID of the issue to delete
 * @returns The tool execution result
 */
export async function deleteIssue(issueId: string): Promise<ToolExecutionResult> {
  return executeTool('issue-manager', 'delete', { issueId });
}

